import React from "react";
import { Checkbox, Typography } from "@mui/material";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TimePicker from "components/TimePcker";
import * as Styled from "../styles";
import { MassEditWorkingHoursFormData, WEEK_DAYS, WeekDayKey } from "../types";
import { TIME_VALIDATION_RULES } from "../constants";

interface DayRowProps {
  dayKey: WeekDayKey;
  control: Control<MassEditWorkingHoursFormData>;
  setValue: UseFormSetValue<MassEditWorkingHoursFormData>;
  isEnabled: boolean;
  isOpen: boolean;
  onEnabledChange: (dayKey: WeekDayKey, enabled: boolean) => void;
  onIsOpenChange: (dayKey: WeekDayKey, isOpen: boolean) => void;
}

const DayRow: React.FC<DayRowProps> = ({
  dayKey,
  control,
  setValue,
  isEnabled,
  isOpen,
  onEnabledChange,
  onIsOpenChange,
}) => {
  const { t } = useTranslation();
  const dayName = WEEK_DAYS[dayKey];

  return (
    <Styled.TimePickerRow>
      <Controller
        name={`days.${dayKey}.enabled`}
        control={control}
        render={({ field }) => (
          <Checkbox
            {...field}
            checked={field.value || false}
            size="small"
            onChange={(e) => {
              field.onChange(e.target.checked);
              onEnabledChange(dayKey, e.target.checked);
            }}
          />
        )}
      />
      <Styled.TimePickerRowContent>
        <Typography
          variant="body2"
          width="200px"
          color="primary.main"
          fontWeight="bold"
        >
          {t(dayName.toLowerCase())}
        </Typography>
        <TimePicker
          control={control}
          name={`days.${dayKey}.open`}
          label="Abre"
          setValue={setValue}
          disabled={!isEnabled || !isOpen}
          rules={{
            minLength: 5,
            ...TIME_VALIDATION_RULES,
            required:
              isEnabled && isOpen ? "Horário de abertura é obrigatório" : false,
          }}
        />
        <Typography variant="body2">até</Typography>
        <TimePicker
          control={control}
          name={`days.${dayKey}.close`}
          label="Fecha"
          setValue={setValue}
          disabled={!isEnabled || !isOpen}
          rules={{
            minLength: 5,
            ...TIME_VALIDATION_RULES,
            required:
              isEnabled && isOpen
                ? "Horário de fechamento é obrigatório"
                : false,
          }}
        />
        <Styled.ContainerButton isActive={isOpen}>
          <Controller
            name={`days.${dayKey}.isOpen`}
            control={control}
            render={({ field }) => (
              <>
                <Styled.CustomSwitch
                  {...field}
                  checked={field.value || false}
                  size="small"
                  color="success"
                  disabled={!isEnabled}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    onIsOpenChange(dayKey, e.target.checked);
                  }}
                />
                <Typography>{field.value ? "Aberto" : "Fechado"}</Typography>
              </>
            )}
          />
        </Styled.ContainerButton>
      </Styled.TimePickerRowContent>
    </Styled.TimePickerRow>
  );
};

export default React.memo(DayRow);
